import { makeStyles } from '@material-ui/core'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import React from 'react'
import TruncatedLabel from './TruncatedLabel'
import type { SelectionTreeNode } from './SelectionTree'

const useStyles = makeStyles(() => ({
    nodeContent: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(6),
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
    },
    groupBadge: {
        backgroundColor: '#E3F2FD',
        color: '#0D47A1',
        fontSize: pxToRem(10),
        fontWeight: 700,
        borderRadius: pxToRem(4),
        padding: `${pxToRem(2)} ${pxToRem(6)}`,
        flexShrink: 0,
        lineHeight: 1.4,
    },
    sectionNumber: {
        color: '#212121',
        fontSize: pxToRem(11),
        fontWeight: 400,
        flexShrink: 0,
    },
    label: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        fontSize: pxToRem(12),
    },
    countBadge: {
        fontSize: pxToRem(10),
        fontWeight: 600,
        borderRadius: pxToRem(10),
        padding: `${pxToRem(2)} ${pxToRem(6)}`,
        flexShrink: 0,
        lineHeight: 1.4,
    },
    expandButton: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        color: '#757575',
        padding: `0 ${pxToRem(4)}`,
        fontSize: pxToRem(18),
    },
}))

/** Props for {@link TreeNodeContentArea}. */
export interface TreeNodeContentAreaProps {
    /** The tree node to display. */
    node: SelectionTreeNode
    /** Number of selected leaf descendants (shown in count badge for parent nodes). */
    selectedCount: number
    /** Total leaf descendants (shown in count badge for parent nodes). */
    total: number
    /** Whether this node's children are currently expanded. */
    expanded: boolean
    /** Called when the expand/collapse toggle is activated. */
    onToggleExpand: () => void
    /** Optional renderer for extra content shown to the right of leaf node labels. */
    renderLeafExtra?: (nodeId: string) => React.ReactNode
}

const COUNT_BADGE_COLORS_NONE = { backgroundColor: '#eaeff5', color: '#525969' }
const COUNT_BADGE_COLORS_ALL = { backgroundColor: '#dbf9a8', color: '#316d15' }
const COUNT_BADGE_COLORS_PARTIAL = { backgroundColor: '#fff1be', color: '#ad5400' }

const getCountBadgeColors = (selectedCount: number, total: number) => {
    if (selectedCount === 0) return COUNT_BADGE_COLORS_NONE
    if (selectedCount === total) return COUNT_BADGE_COLORS_ALL
    return COUNT_BADGE_COLORS_PARTIAL
}

/**
 * Renders the label row content for a tree node: badge, label text, selection count
 * badge (parents only), and expand/collapse toggle (parents only).
 */
const TreeNodeContentArea = ({
    node,
    selectedCount,
    total,
    expanded,
    onToggleExpand,
    renderLeafExtra,
}: TreeNodeContentAreaProps) => {
    const isParent = !!node.children?.length
    const classes = useStyles()
    return (
        <>
            <span className={classes.nodeContent}>
                {node.badge && (
                    isParent
                        ? <span className={classes.groupBadge}>{node.badge}</span>
                        : <span className={classes.sectionNumber}>{node.badge}</span>
                )}
                <TruncatedLabel text={node.label} className={classes.label} />
                {isParent && total > 0 && (
                    <span
                        className={classes.countBadge}
                        style={getCountBadgeColors(selectedCount, total)}
                        aria-label={`${selectedCount} of ${total} selected`}
                    >
                        {selectedCount}/{total}
                    </span>
                )}
            </span>
            {!isParent && renderLeafExtra?.(node.id)}
            {isParent && (
                <span
                    className={classes.expandButton}
                    role="button"
                    tabIndex={0}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    aria-expanded={expanded}
                    onClick={(e) => { e.stopPropagation(); onToggleExpand() }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            e.stopPropagation()
                            onToggleExpand()
                        }
                    }}
                >
                    {expanded ? <ExpandLessIcon fontSize="inherit" aria-hidden="true" /> : <ExpandMoreIcon fontSize="inherit" aria-hidden="true" />}
                </span>
            )}
        </>
    )
}

export default TreeNodeContentArea
